import { ref, watch, onUnmounted, getCurrentInstance } from "vue";
import axios from "axios";
import {
  getCheckpointPresenceSsePath,
  heartbeatCheckpointPresence,
  heartbeatCheckpointEditLock,
} from "@/api/responsibilityAPI";

/** Heartbeat while CheckpointEditFormV2 is open. Stop on leave — backend TTL drops the entry. */
export const PRESENCE_HEARTBEAT_INTERVAL_MS = 15_000;

const RECONNECT_DELAY_MS = 2_000;

function splitDisplayName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/);
  if (!parts.length || !parts[0]) return { first: "", last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

/** Map a presence registry row to PeopleAvatarsButton shape. */
export function presenceRowToAvatar(row) {
  if (!row) return null;
  const { first, last } = splitDisplayName(row.display_name);
  return {
    id: row.access_id,
    accessId: row.access_id,
    img: row.avatar_url || "",
    firstName: first,
    lastName: last,
    fullName: row.display_name || "",
    isStale: Boolean(row.is_stale),
  };
}

/** Normalize SSE lock payload (`presence_snapshot.data.lock` or `lock_changed.data`). */
export function normalizeEditLock(lock) {
  if (lock == null || typeof lock !== "object") {
    return { holder_access_id: null };
  }
  const holderId = lock.holder_access_id ?? null;
  if (holderId == null) {
    return { holder_access_id: null };
  }
  return {
    holder_access_id: holderId,
    holder_display_name: lock.holder_display_name ?? null,
    acquired_at: lock.acquired_at ?? null,
    last_heartbeat_at: lock.last_heartbeat_at ?? null,
    is_stale: Boolean(lock.is_stale),
  };
}

function presenceListToAvatars(list) {
  if (!Array.isArray(list)) return [];
  return list.map(presenceRowToAvatar).filter(Boolean);
}

function upsertByAccessId(list, row) {
  const avatar = presenceRowToAvatar(row);
  if (!avatar) return list;
  const next = list.slice();
  const idx = next.findIndex((a) => String(a.accessId) === String(avatar.accessId));
  if (idx >= 0) {
    next[idx] = avatar;
  } else {
    next.push(avatar);
  }
  return next;
}

function removeByAccessId(list, row) {
  const id = row?.access_id ?? row?.accessId ?? row?.id;
  if (id == null) return list;
  return list.filter((a) => String(a.accessId) !== String(id));
}

function buildPresenceUrl(accountId, checkpointId) {
  const path = getCheckpointPresenceSsePath(accountId, checkpointId).replace(/^\//, "");
  const base = String(axios?.defaults?.baseURL || "").replace(/\/$/, "");
  if (!base) return `/${path}`;
  return `${base}/${path}`;
}

function parseSseChunk(buffer, onEvent) {
  let rest = buffer;
  const parts = rest.split("\n\n");
  rest = parts.pop() || "";

  for (const block of parts) {
    if (!block.trim()) continue;
    let eventName = "message";
    const dataLines = [];
    for (const rawLine of block.split("\n")) {
      const line = rawLine.replace(/\r$/, "");
      if (line.startsWith("event:")) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).trim());
      }
    }
    if (!dataLines.length) {
      if (eventName === "heartbeat") onEvent({ event: "heartbeat", data: null });
      continue;
    }
    const dataStr = dataLines.join("\n");
    let data = null;
    try {
      data = dataStr ? JSON.parse(dataStr) : null;
    } catch {
      data = dataStr;
    }
    onEvent({ event: eventName, data });
  }

  return rest;
}

function resolveParam(value) {
  return typeof value === "function" ? value() : value?.value ?? value;
}

/**
 * CheckpointEditFormV2 presence + edit lock (same SSE channel):
 * - Enter: open SSE + heartbeat immediately (snapshot shows peers + lock)
 * - Every 15s: POST heartbeat-edit-lock/ while holding lock, else presence/heartbeat/
 * - Leave: stop heartbeat + abort SSE + clear avatars/lock (backend TTL removes you for peers)
 * - Listen: presence_changed; checkpoint_updated; any event carrying lock / is_stale
 *
 * @param {{ accountId, checkpointId, enabled?, holdsEditLock?, onCheckpointUpdated?, onPublishMessageReady? }} options
 *   `holdsEditLock` — when true, use edit-lock heartbeat (also refreshes presence TTL).
 *   `onCheckpointUpdated` — called with parsed SSE payload when another client saves.
 *   `onPublishMessageReady` — called with parsed SSE payload when the async publish
 *   message resolves to READY (Rule 12.4a), so the history-icon tooltip can refresh.
 */
export function useCheckpointPresence({
  accountId,
  checkpointId,
  enabled = true,
  holdsEditLock = false,
  onCheckpointUpdated = null,
  onPublishMessageReady = null,
} = {}) {
  const presenceAvatars = ref([]);
  /** null until first snapshot / lock_changed; then normalized lock object. */
  const editLock = ref(null);
  const isConnected = ref(false);
  /** Latest `checkpoint_updated` payload from peers (null until received). */
  const checkpointUpdatedEvent = ref(null);

  let abortController = null;
  let heartbeatTimer = null;
  let reconnectTimer = null;
  let active = false;
  let sessionKey = null;
  let holdsLock = false;
  let updatedCallback = onCheckpointUpdated;
  let publishMessageReadyCallback = onPublishMessageReady;

  function clearHeartbeat() {
    if (heartbeatTimer != null) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  function clearReconnect() {
    if (reconnectTimer != null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function stopStream() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    isConnected.value = false;
  }

  function applyEditLock(lockData) {
    editLock.value = normalizeEditLock(lockData);
  }

  async function sendHeartbeat(resolvedAccountId, resolvedCheckpointId) {
    try {
      if (holdsLock) {
        await heartbeatCheckpointEditLock(resolvedAccountId, resolvedCheckpointId);
      } else {
        await heartbeatCheckpointPresence(resolvedAccountId, resolvedCheckpointId, {});
      }
    } catch (error) {
      if (error?.response?.status && error.response.status < 500) return;
      console.error(
        holdsLock
          ? "Checkpoint edit-lock heartbeat failed:"
          : "Checkpoint presence heartbeat failed:",
        error
      );
    }
  }

  function startHeartbeat(resolvedAccountId, resolvedCheckpointId) {
    clearHeartbeat();
    void sendHeartbeat(resolvedAccountId, resolvedCheckpointId);
    heartbeatTimer = setInterval(() => {
      void sendHeartbeat(resolvedAccountId, resolvedCheckpointId);
    }, PRESENCE_HEARTBEAT_INTERVAL_MS);
  }

  /**
   * Extract lock from any SSE payload that carries it.
   * Snapshot/heartbeat: `data.lock`. lock_changed / lock_is_stale_now: lock fields on `data`.
   * Drive take-over UI off `is_stale` — do not require a specific event name.
   */
  function lockPayloadFromEvent(data) {
    if (data == null || typeof data !== "object") return undefined;
    if (data.lock !== undefined) return data.lock;
    if ("holder_access_id" in data || "is_stale" in data) return data;
    return undefined;
  }

  function applyPresenceEvent({ event, data }) {
    if (event === "presence_snapshot") {
      presenceAvatars.value = presenceListToAvatars(data?.presence);
    } else if (event === "presence_changed") {
      let next = presenceAvatars.value.slice();
      for (const row of data?.removed || []) {
        next = removeByAccessId(next, row);
      }
      for (const row of data?.added || []) {
        next = upsertByAccessId(next, row);
      }
      for (const row of data?.updated || []) {
        next = upsertByAccessId(next, row);
      }
      presenceAvatars.value = next;
    } else if (event === "checkpoint_updated" && data && typeof data === "object") {
      checkpointUpdatedEvent.value = data;
      const cb = typeof updatedCallback === "function" ? updatedCallback : null;
      if (cb) {
        try {
          cb(data);
        } catch (error) {
          console.error("checkpoint_updated handler failed:", error);
        }
      }
    } else if (event === "publish_message_ready" && data && typeof data === "object") {
      const cb =
        typeof publishMessageReadyCallback === "function" ? publishMessageReadyCallback : null;
      if (cb) {
        try {
          cb(data);
        } catch (error) {
          console.error("publish_message_ready handler failed:", error);
        }
      }
    }

    const lockPayload = lockPayloadFromEvent(data);
    if (lockPayload !== undefined) {
      applyEditLock(lockPayload);
    }
  }

  async function openStream(resolvedAccountId, resolvedCheckpointId) {
    stopStream();
    const controller = new AbortController();
    abortController = controller;

    const token = localStorage.getItem("token");
    const url = buildPresenceUrl(resolvedAccountId, resolvedCheckpointId);

    try {
      // Omit Accept: text/event-stream — DRF returns 406 for that media type.
      const response = await fetch(url, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Token ${token}` } : {}),
        },
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`Presence SSE failed with status ${response.status}`);
      }

      isConnected.value = true;
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (active && sessionKey === `${resolvedAccountId}:${resolvedCheckpointId}`) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = parseSseChunk(buffer, applyPresenceEvent);
      }
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error("Checkpoint presence SSE error:", error);
    } finally {
      if (abortController === controller) {
        abortController = null;
        isConnected.value = false;
      }
    }

    if (active && sessionKey === `${resolvedAccountId}:${resolvedCheckpointId}`) {
      clearReconnect();
      reconnectTimer = setTimeout(() => {
        void openStream(resolvedAccountId, resolvedCheckpointId);
      }, RECONNECT_DELAY_MS);
    }
  }

  function stop() {
    active = false;
    sessionKey = null;
    holdsLock = false;
    clearHeartbeat();
    clearReconnect();
    stopStream();
    presenceAvatars.value = [];
    editLock.value = null;
    checkpointUpdatedEvent.value = null;
  }

  function start(resolvedAccountId, resolvedCheckpointId) {
    if (resolvedAccountId == null || resolvedCheckpointId == null) {
      stop();
      return;
    }
    const nextKey = `${resolvedAccountId}:${resolvedCheckpointId}`;
    if (active && sessionKey === nextKey) return;

    stop();
    active = true;
    sessionKey = nextKey;
    holdsLock = Boolean(resolveParam(holdsEditLock));
    startHeartbeat(resolvedAccountId, resolvedCheckpointId);
    void openStream(resolvedAccountId, resolvedCheckpointId);
  }

  const stopWatch = watch(
    () => {
      const aid = resolveParam(accountId);
      const cid = resolveParam(checkpointId);
      const on = resolveParam(enabled);
      const holding = Boolean(resolveParam(holdsEditLock));
      return { aid, cid, on: on !== false, holding };
    },
    ({ aid, cid, on, holding }, prev) => {
      if (!on || aid == null || cid == null) {
        stop();
        return;
      }
      const nextKey = `${aid}:${cid}`;
      const sameSession = active && sessionKey === nextKey;
      holdsLock = holding;
      if (!sameSession) {
        start(aid, cid);
        return;
      }
      // Holder flipped mid-session — restart heartbeat cadence with the right endpoint.
      if (prev && prev.holding !== holding) {
        startHeartbeat(aid, cid);
      }
    },
    { immediate: true }
  );

  try {
    if (getCurrentInstance()) {
      onUnmounted(() => {
        stopWatch();
        stop();
      });
    }
  } catch {
    // Called outside setup (unit tests) — callers must invoke stop().
  }

  return {
    presenceAvatars,
    editLock,
    checkpointUpdatedEvent,
    isConnected,
    stop,
  };
}
